import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface NavigationProps {
  showPublicBooking?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ showPublicBooking = false }) => {
  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll esemény figyelése
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Hiba a kijelentkezés során:', error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
      ? 'bg-white/95 backdrop-blur-sm shadow-lg'
      : 'bg-white shadow-md'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center mr-2 shadow">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h1 className="text-xl font-bold text-red-600">SHS Gokart</h1>
              </div>
            </div>
            <div className="hidden md:ml-6 md:flex md:space-x-1">
              {showPublicBooking && (
                <button
                  onClick={() => navigate('/public-booking')}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/public-booking')
                    ? 'text-red-600 border-b-2 border-red-500'
                    : 'text-gray-600 hover:text-red-600'
                    } rounded-md`}
                >
                  Foglalás
                </button>
              )}
              {currentUser && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/dashboard')
                    ? 'text-red-600 border-b-2 border-red-500'
                    : 'text-gray-600 hover:text-red-600'
                    } rounded-md`}
                >
                  Vezérlőpult
                </button>
              )}
              {isAdmin && (
                <>
                  <button
                    onClick={() => navigate('/bookings')}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/bookings')
                      ? 'text-red-600 border-b-2 border-red-500'
                      : 'text-gray-600 hover:text-red-600'
                      } rounded-md`}
                  >
                    Foglalások
                  </button>
                  <button
                    onClick={() => navigate('/users')}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/users')
                      ? 'text-red-600 border-b-2 border-red-500'
                      : 'text-gray-600 hover:text-red-600'
                      } rounded-md`}
                  >
                    Felhasználók
                  </button>
                  <button
                    onClick={() => navigate('/racers')}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/racers')
                      ? 'text-red-600 border-b-2 border-red-500'
                      : 'text-gray-600 hover:text-red-600'
                      } rounded-md`}
                  >
                    Vendégek
                  </button>
                  <button
                    onClick={() => navigate('/settings')}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/settings')
                      ? 'text-red-600 border-b-2 border-red-500'
                      : 'text-gray-600 hover:text-red-600'
                      } rounded-md`}
                  >
                    Beállítások
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Jobb oldali elemek */}
          <div className="flex items-center">
            {currentUser ? (
              <div className="flex items-center space-x-4">
                <div className="hidden md:flex md:items-center md:space-x-2">
                  <button
                    onClick={() => navigate('/profile')}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive('/profile')
                      ? 'text-red-600 border-b-2 border-red-500'
                      : 'text-gray-600 hover:text-red-600'
                      } rounded-md flex items-center`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    Profil
                  </button>
                  <button
                    onClick={handleLogout}
                    className="px-3 py-2 text-sm font-medium text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414l-5-5H3zm7 5a1 1 0 00-1 1v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 12.586V9a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Kilépés
                  </button>
                </div>

                <div className="flex items-center space-x-2 border-l pl-4 ml-2">
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-medium text-gray-900">{currentUser.displayName}</span>
                    <span className="text-xs text-gray-500">{isAdmin ? 'Admin' : 'Felhasználó'}</span>
                  </div>
                  {currentUser.photoURL ? (
                    <img
                      className="h-9 w-9 rounded-full border-2 border-gray-200 shadow-sm object-cover"
                      src={currentUser.photoURL}
                      alt={currentUser.displayName || 'Felhasználó'}
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold border-2 border-gray-200 shadow-sm">
                      {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors duration-200 flex items-center shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V3zm5 4a1 1 0 10-2 0v6a1 1 0 102 0V7zm4-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Bejelentkezés
                </button>

                <button
                  onClick={() => navigate('/registration')}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors duration-200 flex items-center shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  Regisztráció
                </button>
              </div>
            )}

            {/* Mobil menü gomb */}
            {currentUser && (
              <div className="md:hidden">
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 rounded-md text-gray-500 hover:bg-gray-100 transition-colors duration-200"
                  aria-label="Menü megnyitása"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobil menü */}
        {mobileMenuOpen && currentUser && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              {showPublicBooking && (
                <button
                  onClick={() => {
                    navigate('/public-booking');
                    setMobileMenuOpen(false);
                  }}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/public-booking')
                    ? 'text-red-600 bg-red-50'
                    : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                    } w-full text-left`}
                >
                  Foglalás
                </button>
              )}
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/dashboard')
                  ? 'text-red-600 bg-red-50'
                  : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  } w-full text-left`}
              >
                Vezérlőpult
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      navigate('/bookings');
                      setMobileMenuOpen(false);
                    }}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/bookings')
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                      } w-full text-left`}
                  >
                    Foglalások
                  </button>
                  <button
                    onClick={() => {
                      navigate('/users');
                      setMobileMenuOpen(false);
                    }}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/users')
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                      } w-full text-left`}
                  >
                    Felhasználók
                  </button>
                  <button
                    onClick={() => {
                      navigate('/racers');
                      setMobileMenuOpen(false);
                    }}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/racers')
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                      } w-full text-left`}
                  >
                    Vendégek
                  </button>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setMobileMenuOpen(false);
                    }}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/settings')
                      ? 'text-red-600 bg-red-50'
                      : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                      } w-full text-left`}
                  >
                    Beállítások
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  navigate('/profile');
                  setMobileMenuOpen(false);
                }}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive('/profile')
                  ? 'text-red-600 bg-red-50'
                  : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
                  } w-full text-left`}
              >
                Profil
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="block px-3 py-2 rounded-md text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 w-full text-left transition-colors duration-200"
              >
                Kilépés
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation; 